/* eslint-disable max-statements */
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import { useContext, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { DashboardTypes } from "./types";
import { useGetSelectedDealFundId } from "./use-current-asset-dashboard";
import { dashboardPageTypeDefinitionsByType, useGetAvailableCurrencies, useGetEntityInfo } from "./use-get-dashboards-by-type";
import { AssetClass } from "api/ux/src";
import { preview } from "common/constants";
import { getDateWhenString } from "common/date-utils";
import { capitalizeFirstLetter } from "common/string-helper";
import { dashboardTypeToEntityType } from "hooks/shared/dashboardTypeToEntityType";
import { tenantModulesSelector } from "hooks/use-tenant-settings";
import { UxContextComponent } from "plugins/context";
export const useCurrentDashboardEntityId = () => useSelector((state) => state.currentDashboard.currentEntityId);
export const useCurrentDashboardOptions = () => useSelector((state) => state.currentDashboard.dashboardOptions, shallowEqual);
export const useCurrentDashboardCurrency = () => useSelector((state) => state.currentDashboard.currency);
const existingSectionsIdsSelector = createSelector((state) => state.currentDashboard.currentStatus.sections, (sections) => Object.keys(sections || {}));
export const useExistingSectionsIds = () => useSelector(existingSectionsIdsSelector, shallowEqual);
export const useEditMode = () => useSelector((state) => state.currentDashboard.isEditMode);
const isMaximizedModeSelector = (state) => state.currentDashboard.isMaximizedMode;
const maximizedWidgetSelector = (state) => state.currentDashboard.maximizedWidget;
export const useMaximizedWidgetMode = (sectionId, widgetId) => {
    const maximizedWidget = createSelector(isMaximizedModeSelector, maximizedWidgetSelector, (isMaximizedMode, widget) => isMaximizedMode && widget?.sectionId === sectionId && widget?.widgetId === widgetId);
    return useSelector(maximizedWidget);
};
export const useGetSelectedCurrency = () => {
    const availableCurrencies = useGetAvailableCurrencies(), [firstCurrency] = availableCurrencies, selectedCurrency = useSelector((state) => state.currentDashboard.currency);
    if (selectedCurrency && availableCurrencies.find((currency) => currency.currencyCode === selectedCurrency)) {
        return selectedCurrency;
    }
    return firstCurrency?.currencyCode;
};
const getDateSelector = (selector) => createSelector(selector, (referenceDate) => getDateWhenString(referenceDate));
const defaultDateSelector = (state) => state.currentDashboard.referenceDate;
export const useGetSelectedReferenceDate = () => {
    let selector = defaultDateSelector;
    const { dashboardDateSelectorOverride } = useContext(UxContextComponent);
    if (dashboardDateSelectorOverride) {
        selector = dashboardDateSelectorOverride;
    }
    const dateSelector = useMemo(() => getDateSelector(selector), [selector]);
    return useSelector(dateSelector, isEqual);
};
export const useGetSelectedDashboardOptions = () => {
    const selectedFundId = useGetSelectedDealFundId(), selectedOptions = useCurrentDashboardOptions();
    return selectedFundId ? { selectedFundId } : selectedOptions;
};
export const useCurrentEntity = () => useSelector((state) => state.entities?.[dashboardTypeToEntityType(capitalizeFirstLetter(state?.currentDashboard?.currentType))]?.[state.currentDashboard.currentEntityId], isEqual);
export const useMaximizedMode = () => useSelector(isMaximizedModeSelector);
const pathSelector = createSelector((state) => state.router.location.pathname, (pathname) => pathname.split("/"));
const templateSelector = createSelector((state) => state.entities.Template, (state) => state.entities.Section, tenantModulesSelector, (analysis, section, modules) => {
    const result = {};
    for (const [key, type] of Object.entries(analysis)) {
        const mappedAnalysis = type.analysis?.map((x) => ({
            ...x,
            sections: x.sections?.filter((se) => {
                const customOrDefaultSection = se.requiredModules?.length ? se.requiredModules : section[se.id || ""]?.requiredModules;
                if (!customOrDefaultSection || customOrDefaultSection?.length === 0) {
                    return true;
                }
                return customOrDefaultSection?.every((module) => modules.includes(module));
            })
        }));
        result[key] = {
            ...type,
            analysis: mappedAnalysis?.filter((x) => x.sections && x.sections?.length > 0)
        };
    }
    return result;
});
export const filterAvailableAnalysesByAssetClass = (analyses, entityAssetClasses) => {
    if (!analyses || !entityAssetClasses || entityAssetClasses.length === 0) {
        return analyses;
    }
    if (entityAssetClasses.length === 1) {
        return analyses.filter((x) => x.applicableAssetClasses && (entityAssetClasses.some((e) => x.applicableAssetClasses?.includes(e)) ||
            x.applicableAssetClasses?.includes(AssetClass.ALL) || x.applicableAssetClass === AssetClass.ALL));
    }
    return analyses.filter((x) => (!x.applicableAssetClasses?.length || x.applicableAssetClasses.includes(AssetClass.ALL) ||
        entityAssetClasses.some((e) => x.applicableAssetClasses?.includes(e))) && (x.hideWhenMultipleAssetClasses === undefined || x.hideWhenMultipleAssetClasses === false));
};
export const pathInformationSelector = (state) => {
    const pathArray = pathSelector(state);
    let selectedType = pathArray.length > 1
        ? pathArray[1].split("-").map((word) => capitalizeFirstLetter(word))
            .join("")
        : "";
    const entityInfo = dashboardPageTypeDefinitionsByType[selectedType]?.entityInfoSelector(state);
    const analysis = templateSelector(state);
    const [, , , workspaceInUrl] = pathArray;
    let sectionInUrl = pathArray?.[4];
    if (pathArray?.[3] === "pacing") {
        sectionInUrl = "pacing";
    }
    let analysesForType = {};
    analysesForType = analysis[String(entityInfo?.type)];
    const analysesForSpecificSubtype = analysis[String(entityInfo?.type) + entityInfo?.subtype];
    if (analysesForSpecificSubtype) {
        analysesForType = analysesForSpecificSubtype;
        selectedType += entityInfo?.subtype;
    }
    const defaultSectionId = entityInfo && filterAvailableAnalysesByAssetClass(analysesForType?.analysis, entityInfo.assetClasses)?.[0]?.sections?.[0]?.id;
    return { pathInformations: pathArray,
        selectedEntityId: pathArray[2],
        selectedSection: sectionInUrl ?? defaultSectionId,
        selectedType,
        selectedWorkspace: workspaceInUrl || undefined };
};
export const useDashboardFullPathInformations = () => useSelector(pathInformationSelector, shallowEqual);
export const useCurrentDashboardSection = () => {
    const fullPathInfo = useDashboardFullPathInformations();
    return fullPathInfo?.selectedSection;
};
export const pathnameToType = (pathname) => pathname.split("/")?.[1]?.split("-")
    .map((word) => capitalizeFirstLetter(word))
    .join("") || "";
const currentDashboardTypeSelector = createSelector((state) => state.router.location.pathname, (pathname) => pathnameToType(pathname));
export const useCurrentDashboardType = () => useSelector(currentDashboardTypeSelector);
const useCurrentDashboardCurrentType = () => useSelector((state) => state.currentDashboard.currentType);
export const useIsVariationColumnPresentInWidgetPreview = () => useSelector((state) => (state.workshop.editor.preview?.widget?.componentProps)
    .columnsTemplate?.columns.some((column) => column.variation === true));
export const useGetEntityLatestDataDate = () => {
    const options = useCurrentDashboardOptions();
    const investorId = options?.enabled && options?.selectedInvestorId;
    const currentType = capitalizeFirstLetter(useCurrentDashboardCurrentType());
    const entity = useCurrentEntity();
    switch (currentType) {
        case DashboardTypes.Fund:
            if (investorId) {
                return entity?.subscriptions?.find((x) => x.investmentId === investorId)?.lastInvestorCashFlowDate;
            }
            return entity?.latestCapitalAccountReportedDate;
        case DashboardTypes.Portfolio:
            return entity?.latestInvestorCfDate;
        case DashboardTypes.Manager:
            return entity?.latestCapitalAccountReportedDate;
        case DashboardTypes.FundSeries:
            return entity?.referenceDate;
        default:
            return null;
    }
};
export const analysisSelector = (type) => createSelector(templateSelector, (analysis) => analysis[type] || {});
export const useCurrentDashboardTemplate = () => {
    let type = useCurrentDashboardType();
    const entityInfo = useGetEntityInfo();
    const analysesForSpecificSubtype = useSelector((state) => state.entities.Template[type + entityInfo?.subtype], isEqual);
    if (entityInfo?.subtype !== undefined && analysesForSpecificSubtype) {
        type += entityInfo.subtype;
    }
    return useSelector(analysisSelector(type));
};
export const useCurrentDashboardAnalysis = () => {
    const template = useCurrentDashboardTemplate();
    const currentAnalysis = useSelector((state) => state.currentDashboard.currentAnalysis) ?? 0;
    const presentAnalysis = useSelector((state) => state.designer.present.analysis, isEqual);
    return template?.analysis?.[currentAnalysis] ?? presentAnalysis;
};
export const useCurrentDashboardPreviweWidget = () => useSelector((state) => state.currentDashboard.currentStatus.sections?.[preview.sectionId]?.widgets?.[preview.widgetId]);
export const useCurrentStatusSelectedGroupedWidget = () => useSelector((state) => state.currentDashboard.currentStatus.sections);
export const useCurrentStatusSections = () => useSelector((state) => state.currentDashboard?.currentStatus?.sections || {});
export const includeHistoricalCashflowsSelector = (sectionId) => (state) => state.currentDashboard?.currentStatus?.sections?.[sectionId]?.includeHistoricalCashflows ?? true;
export const useIncludeHistoricalCashflows = (sectionId) => useSelector(includeHistoricalCashflowsSelector(sectionId));
export const includePacingPlanSelector = (sectionId) => (state) => state.currentDashboard?.currentStatus?.sections?.[sectionId]?.includePacingPlan ?? false;
export const useIncludePacingPlan = (sectionId) => useSelector(includePacingPlanSelector(sectionId));
